import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { IoArrowForward, IoLogoDiscord, IoSparkles, IoCreateOutline, IoClose, IoAddOutline, IoTrashOutline, IoFlashOutline } from "react-icons/io5";
import DojoShell, { RED } from "@/components/DojoShell";
import { useAuth } from "@/context/AuthContext";
import { getPageConfig, updatePageConfig } from "@/lib/api";

export default function Communaute() {
  const { isAdmin } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { getPageConfig("communaute").then(setCfg).catch(() => {}); }, []);

  const join = () => cfg?.discord_url && window.open(cfg.discord_url, "_blank");
  const openEdit = () => { setDraft(JSON.parse(JSON.stringify(cfg))); setEditing(true); };
  const saveEdit = async () => {
    setBusy(true);
    try { const saved = await updatePageConfig("communaute", draft); setCfg(saved); setEditing(false); toast.success("Page Communauté mise à jour"); }
    catch { toast.error("Échec de l'enregistrement"); } finally { setBusy(false); }
  };

  if (!cfg) return <DojoShell crumbs={[{ label: "Communauté" }]}><p style={{ color: "var(--text-secondary)" }}>Chargement…</p></DojoShell>;
  const activities = cfg.activities || [];
  const order = cfg.sections && cfg.sections.length ? cfg.sections : ["hero", "activites", "discord"];

  const renderSection = (sec) => {
    if (sec === "hero") return (
      <div style={s.hero} data-testid="communaute-hero">
        <span style={s.heroKanji}>縁</span>
        <div style={s.heroGlow} />
        <div style={{ position: "relative", zIndex: 1, maxWidth: 700 }}>
          <div style={s.eyebrow}><IoSparkles size={13} /> {cfg.eyebrow}</div>
          <h1 style={s.h1}>{cfg.title}</h1>
          <p style={s.heroSub}>{cfg.intro}</p>
        </div>
      </div>
    );
    if (sec === "activites") return (
      <div>
        <h2 style={s.sectionTitle}>Les activités du moment</h2>
        <div style={s.grid} data-testid="communaute-activities">
          {activities.map((a, i) => (
            <div key={i} style={s.actCard}>
              <span style={s.actIcon}><IoFlashOutline size={22} /></span>
              <div style={s.actTitle}>{a.title}</div>
              <div style={s.actText}>{a.text}</div>
            </div>
          ))}
          {activities.length === 0 && <p style={{ color: "var(--text-secondary)" }}>Aucune activité annoncée pour l'instant.</p>}
        </div>
      </div>
    );
    if (sec === "discord") return (
      <div style={s.discord} data-testid="communaute-discord">
        <div style={s.discordGlow} />
        <div style={s.discordInner}>
          <span style={s.discordIcon}><IoLogoDiscord size={54} /></span>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={s.discordTitle}>{cfg.discord_title}</div>
            <div style={s.discordSub}>{cfg.discord_sub}</div>
          </div>
          <button style={s.discordBtn} data-testid="communaute-discord-btn" onClick={join}>
            <IoLogoDiscord size={22} /> Rejoindre le Discord <IoArrowForward size={18} />
          </button>
        </div>
        <p style={s.discordNote}>Le lien s'ouvre dans un nouvel onglet — tu ne perds pas ta place au Dōjō. ⛩</p>
      </div>
    );
    return null;
  };

  return (
    <DojoShell crumbs={[{ label: "Communauté" }]}>
      {isAdmin && (
        <button style={ed.editPageBtn} data-testid="communaute-edit" onClick={openEdit}><IoCreateOutline size={16} /> Modifier cette page</button>
      )}

      {order.map((sec) => (
        <div key={sec} style={{ marginBottom: 34 }}>{renderSection(sec)}</div>
      ))}

      {editing && draft && <EditModal draft={draft} setDraft={setDraft} onClose={() => setEditing(false)} onSave={saveEdit} busy={busy} />}
    </DojoShell>
  );
}

function EditModal({ draft, setDraft, onClose, onSave, busy }) {
  const set = (k, v) => setDraft({ ...draft, [k]: v });
  const setAct = (i, field, v) => { const l = [...(draft.activities || [])]; l[i] = { ...l[i], [field]: v }; set("activities", l); };
  const addAct = () => set("activities", [...(draft.activities || []), { title: "Nouvelle activité", text: "" }]);
  const delAct = (i) => set("activities", (draft.activities || []).filter((_, x) => x !== i));

  const SEC_LABELS = { hero: "Présentation", activites: "Activités du moment", discord: "Bloc Discord" };
  const ALL_SECS = ["hero", "activites", "discord"];
  const secs = draft.sections && draft.sections.length ? draft.sections : ALL_SECS;
  const move = (i, dir) => { const l = [...secs]; const j = i + dir; if (j < 0 || j >= l.length) return; [l[i], l[j]] = [l[j], l[i]]; set("sections", l); };
  const removeSec = (k) => set("sections", secs.filter((x) => x !== k));
  const addSec = (k) => set("sections", [...secs, k]);
  const hidden = ALL_SECS.filter((k) => !secs.includes(k));

  return (
    <div style={ed.overlay} onClick={onClose}>
      <div style={ed.modal} onClick={(e) => e.stopPropagation()} data-testid="communaute-edit-modal">
        <div style={ed.head}>
          <span style={ed.title}>Modifier la page Communauté</span>
          <button style={ed.x} onClick={onClose} data-testid="communaute-edit-close"><IoClose size={20} /></button>
        </div>
        <div style={ed.body}>
          <div style={ed.dividerRow}><span style={ed.dividerLabel}>Sections & ordre</span></div>
          <div data-testid="communaute-sections-editor" style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 6 }}>
            {secs.map((k, i) => (
              <div key={k} style={ed.secRow} data-testid={`section-row-${k}`}>
                <span style={ed.secName}>{SEC_LABELS[k] || k}</span>
                <div style={{ display: "flex", gap: 6 }}>
                  <button style={{ ...ed.iconBtn, opacity: i === 0 ? 0.4 : 1 }} disabled={i === 0} onClick={() => move(i, -1)} data-testid={`section-up-${k}`}>↑</button>
                  <button style={{ ...ed.iconBtn, opacity: i === secs.length - 1 ? 0.4 : 1 }} disabled={i === secs.length - 1} onClick={() => move(i, 1)} data-testid={`section-down-${k}`}>↓</button>
                  <button style={ed.iconBtnDel} onClick={() => removeSec(k)} title="Masquer la section" data-testid={`section-remove-${k}`}><IoTrashOutline size={14} /></button>
                </div>
              </div>
            ))}
            {hidden.length > 0 && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
                {hidden.map((k) => (
                  <button key={k} style={ed.addBtn} onClick={() => addSec(k)} data-testid={`section-add-${k}`}><IoAddOutline size={14} /> {SEC_LABELS[k] || k}</button>
                ))}
              </div>
            )}
          </div>

          <div style={ed.dividerRow}><span style={ed.dividerLabel}>Contenu</span></div>
          <label style={ed.lbl}>En-tête</label>
          <input style={ed.input} data-testid="com-edit-eyebrow" value={draft.eyebrow || ""} onChange={(e) => set("eyebrow", e.target.value)} />
          <label style={ed.lbl}>Titre</label>
          <input style={ed.input} data-testid="com-edit-title" value={draft.title || ""} onChange={(e) => set("title", e.target.value)} />
          <label style={ed.lbl}>Présentation</label>
          <textarea style={{ ...ed.input, resize: "vertical" }} rows={3} data-testid="com-edit-intro" value={draft.intro || ""} onChange={(e) => set("intro", e.target.value)} />

          <div style={ed.dividerRow}>
            <span style={ed.dividerLabel}>Activités du moment</span>
            <button style={ed.addBtn} onClick={addAct}><IoAddOutline size={15} /> Ajouter</button>
          </div>
          {(draft.activities || []).map((it, i) => (
            <div key={i} style={ed.itemRow}>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                <input style={ed.input} placeholder="Titre" value={it.title || ""} onChange={(e) => setAct(i, "title", e.target.value)} />
                <input style={ed.input} placeholder="Description" value={it.text || ""} onChange={(e) => setAct(i, "text", e.target.value)} />
              </div>
              <button style={ed.delBtn} onClick={() => delAct(i)}><IoTrashOutline size={16} /></button>
            </div>
          ))}

          <div style={ed.dividerRow}><span style={ed.dividerLabel}>Bloc Discord</span></div>
          <label style={ed.lbl}>Lien d'invitation Discord</label>
          <input style={ed.input} data-testid="com-edit-url" value={draft.discord_url || ""} onChange={(e) => set("discord_url", e.target.value)} placeholder="https://discord.gg/..." />
          <label style={ed.lbl}>Titre du bloc Discord</label>
          <input style={ed.input} value={draft.discord_title || ""} onChange={(e) => set("discord_title", e.target.value)} />
          <label style={ed.lbl}>Sous-titre du bloc Discord</label>
          <textarea style={{ ...ed.input, resize: "vertical" }} rows={2} value={draft.discord_sub || ""} onChange={(e) => set("discord_sub", e.target.value)} />
        </div>
        <div style={ed.actions}>
          <button style={ed.cancel} onClick={onClose}>Annuler</button>
          <button style={{ ...ed.save, opacity: busy ? 0.6 : 1 }} disabled={busy} data-testid="communaute-save" onClick={onSave}>{busy ? "Enregistrement…" : "Enregistrer"}</button>
        </div>
      </div>
    </div>
  );
}

const s = {
  hero: { position: "relative", overflow: "hidden", marginBottom: 34, padding: "40px 30px", borderRadius: 26, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", boxShadow: "var(--surface-shadow)" },
  heroKanji: { position: "absolute", right: -10, top: -34, fontSize: 190, fontWeight: 800, color: "var(--text-primary)", opacity: 0.05, lineHeight: 1, fontFamily: "var(--font-display)", pointerEvents: "none" },
  heroGlow: { position: "absolute", bottom: -90, left: -50, width: 320, height: 320, borderRadius: "50%", background: "radial-gradient(circle, color-mix(in srgb, var(--brand) 22%, transparent), transparent 70%)" },
  eyebrow: { display: "inline-flex", alignItems: "center", gap: 6, color: RED, fontWeight: 800, fontSize: 12, letterSpacing: 2 },
  h1: { fontSize: 40, fontWeight: 900, margin: "10px 0 12px", color: "var(--text-primary)", fontFamily: "var(--font-display)", lineHeight: 1.05 },
  heroSub: { color: "var(--text-secondary)", fontSize: 16, lineHeight: 1.65, margin: 0 },
  sectionTitle: { fontSize: 14, fontWeight: 800, letterSpacing: 1.5, textTransform: "uppercase", color: "var(--text-secondary)", margin: "0 0 16px" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 16, marginBottom: 38 },
  actCard: { padding: "22px 20px", borderRadius: 18, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", boxShadow: "var(--surface-shadow)" },
  actIcon: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 46, height: 46, borderRadius: 14, background: "color-mix(in srgb, var(--brand) 12%, transparent)", color: RED, marginBottom: 14 },
  actTitle: { fontSize: 16.5, fontWeight: 800, color: "var(--text-primary)", marginBottom: 6 },
  actText: { fontSize: 13.5, lineHeight: 1.55, color: "var(--text-secondary)" },
  discord: { position: "relative", overflow: "hidden", borderRadius: 26, padding: "36px 30px", background: "linear-gradient(135deg, #5865F2, #404EED)", boxShadow: "0 24px 60px rgba(88,101,242,0.4)" },
  discordGlow: { position: "absolute", top: -80, right: -40, width: 320, height: 320, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,255,255,0.22), transparent 70%)" },
  discordInner: { position: "relative", zIndex: 1, display: "flex", alignItems: "center", gap: 22, flexWrap: "wrap" },
  discordIcon: { color: "#fff", flexShrink: 0, opacity: 0.95 },
  discordTitle: { color: "#fff", fontSize: 26, fontWeight: 900, fontFamily: "var(--font-display)", lineHeight: 1.1 },
  discordSub: { color: "rgba(255,255,255,0.9)", fontSize: 15, marginTop: 8, lineHeight: 1.55, maxWidth: 520 },
  discordBtn: { display: "inline-flex", alignItems: "center", gap: 10, background: "#fff", color: "#404EED", fontWeight: 900, fontSize: 16, padding: "16px 26px", borderRadius: 999, border: "none", cursor: "pointer", whiteSpace: "nowrap", boxShadow: "0 10px 24px rgba(0,0,0,0.2)" },
  discordNote: { position: "relative", zIndex: 1, color: "rgba(255,255,255,0.82)", fontSize: 12.5, marginTop: 18, textAlign: "center" },
};

const ed = {
  editPageBtn: { display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 16, padding: "9px 14px", borderRadius: 10, border: `1.5px solid ${RED}`, background: "var(--bg-secondary)", color: RED, fontWeight: 800, fontSize: 13, cursor: "pointer" },
  overlay: { position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(3px)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "36px 16px", overflowY: "auto" },
  modal: { width: "100%", maxWidth: 620, background: "var(--bg-secondary)", borderRadius: 18, boxShadow: "0 30px 80px rgba(0,0,0,0.5)", overflow: "hidden" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid var(--border-c)" },
  title: { fontSize: 16.5, fontWeight: 900, color: "var(--text-primary)" },
  x: { width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "1px solid var(--border-c)", background: "var(--bg-tertiary)", cursor: "pointer", color: "var(--text-secondary)" },
  body: { padding: 20, display: "flex", flexDirection: "column", gap: 8, maxHeight: "68vh", overflowY: "auto" },
  lbl: { fontSize: 12.5, fontWeight: 700, color: "var(--text-secondary)", marginTop: 6 },
  input: { width: "100%", padding: "10px 12px", border: "1.5px solid var(--border-c)", borderRadius: 10, fontSize: 14, fontFamily: "inherit", color: "var(--text-primary)", background: "var(--bg-tertiary)", boxSizing: "border-box" },
  dividerRow: { display: "flex", alignItems: "center", justifyContent: "space-between", margin: "16px 0 6px", paddingTop: 14, borderTop: "1px solid var(--border-c)" },
  dividerLabel: { fontSize: 13, fontWeight: 900, color: "var(--brand)", textTransform: "uppercase", letterSpacing: 1 },
  addBtn: { display: "inline-flex", alignItems: "center", gap: 4, padding: "6px 10px", borderRadius: 8, border: "1px solid var(--border-c)", background: "var(--bg-tertiary)", color: "var(--text-primary)", fontSize: 12, fontWeight: 700, cursor: "pointer" },
  itemRow: { display: "flex", gap: 8, padding: 12, borderRadius: 12, border: "1px solid var(--border-c)", background: "var(--bg-tertiary)", marginBottom: 8 },
  delBtn: { width: 34, height: 34, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", color: RED, cursor: "pointer" },
  actions: { display: "flex", justifyContent: "flex-end", gap: 10, padding: "14px 20px", borderTop: "1px solid var(--border-c)" },
  cancel: { padding: "11px 18px", borderRadius: 10, border: "1.5px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-secondary)", fontWeight: 700, cursor: "pointer" },
  save: { padding: "11px 22px", borderRadius: 10, border: "none", background: RED, color: "#fff", fontWeight: 800, cursor: "pointer" },
  secRow: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "10px 12px", borderRadius: 10, border: "1px solid var(--border-c)", background: "var(--bg-tertiary)" },
  secName: { fontSize: 13.5, fontWeight: 700, color: "var(--text-primary)" },
  iconBtn: { width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontWeight: 800, cursor: "pointer", fontSize: 15 },
  iconBtnDel: { width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", color: RED, cursor: "pointer" },
};
